// src/components/ReportModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, FileText, CheckCircle, Printer } from 'lucide-react';
import ReportLoadingDialog from './ReportLoadingDialog';
import ReportSettingsModal from './ReportSettingsModal';
import { generateFullReport } from '../utils/reportUtils';
import { calculateStatistics } from '../utils/statisticsUtils';
import { toast } from 'react-hot-toast';

const ReportModal = ({
    isOpen,
    onClose,
    organization,
    user,
    features,
    responses,
    teamResponses,
    comments,
    reactions,
    dependencies,
    reportHistory,
    onReportHistoryChange
}) => {
    const [error, setError] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);

    // Handle click outside
    const handleBackdropClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    }, [onClose]);

    // Format date helper
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            return date.toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Date formatting error:', error);
            return dateString;
        }
    };

    useEffect(() => {
        if (isOpen) {
            onReportHistoryChange?.();
        }
    }, [isOpen, onReportHistoryChange]);

    // Select the most recent report when the modal opens
    useEffect(() => {
        if (isOpen && reportHistory.length > 0) {
            const latestReport = reportHistory[0];
            setSelectedReport(latestReport);
            setPdfUrl(`/api/reports/view/${latestReport.id}`);
        }
    }, [isOpen, reportHistory]);

    const handleSelectReport = (report) => {
        setSelectedReport(report);
        setPdfUrl(`/api/reports/view/${report.id}`);
    };

    const handleDownloadReport = (report) => {
        window.open(`/api/reports/download/${report.id}`, '_blank');
    };

    const handlePrintReport = () => {
        if (selectedReport) {
            const printWindow = window.open(`/api/reports/view/${selectedReport.id}`, '_blank');
            printWindow.onload = () => {
                printWindow.print();
            };
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={handleBackdropClick}
        >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-semibold">Report History</h2>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex h-[calc(90vh-8rem)]">
                    {/* Report List */}
                    <div className="w-1/3 border-r p-4 overflow-y-auto">
                        <div className="space-y-3">
                            {reportHistory.map(report => (
                                <div
                                    key={report.id}
                                    className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer transition-colors ${
                                        selectedReport?.id === report.id ? 'ring-2 ring-blue-500' : ''
                                    }`}
                                    onClick={() => handleSelectReport(report)}
                                >
                                    <div className="flex items-center gap-3">
                                        <FileText className="w-5 h-5 text-gray-600" />
                                        <div>
                                            <div className="text-sm font-medium">
                                                {report.generated_at_formatted || formatDate(report.generated_at)}
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                Generated by {report.user_name || 'Unknown User'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownloadReport(report);
                                            }}
                                            className="inline-flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                                        >
                                            <Download className="w-4 h-4" />
                                            Download
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* PDF Preview */}
                    <div className="w-2/3 p-4 flex flex-col">
                        {error ? (
                            <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
                                {error}
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-medium">Preview</h3>
                                    {selectedReport && (
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleDownloadReport(selectedReport)}
                                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                            >
                                                <Download className="w-4 h-4" />
                                                Download Report
                                            </button>
                                            <button
                                                onClick={handlePrintReport}
                                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                            >
                                                <Printer className="w-4 h-4" />
                                                Print Report
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {pdfUrl ? (
                                    <div className="flex-1 bg-gray-100 rounded-lg overflow-hidden">
                                        <iframe
                                            src={pdfUrl}
                                            className="w-full h-full"
                                            title="PDF Preview"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex-1 flex items-center justify-center text-gray-500">
                                        Select a report to preview
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReportModal;